import React from 'react';
import PropTypes from 'prop-types';
import { useTheme, alpha, Box } from '@mui/material';
import AppBoxComponent from 'components/mui/AppBoxComponent';
import AppTypographyComponent from 'components/mui/AppTypographyComponent';
import AppButtonComponent from 'components/mui/AppButtonComponent';
import { DescriptionOutlined, Close } from '@mui/icons-material';
import { typography } from 'theme';
import Nodisplay from 'assets/images/nodisplay.svg';

/**
 * NodisplayComponent - Reusable empty state component
 * Displays an empty state with icon, title, subtitle, and optional button
 * @component
 * @param {Object} props - Component props
 * @param {string} props.title - Title text to display
 * @param {string} props.subtitle - Subtitle/description text to display
 * @param {string} props.buttonText - Optional button text (button only shows if provided)
 * @param {Function} props.onButtonClick - Optional button click handler (button only shows if provided)
 * @param {Object} props.containerSx - Optional custom styles for container
 * @returns {React.ReactElement} NodisplayComponent
 */
const NodisplayComponent = ({
  title,
  subtitle,
  buttonText,
  onButtonClick,
  containerSx = {},
  titleSx = {},
  subtitleSx = {},
  imageHeight,
}) => {
  const theme = useTheme();

  // Show button only if both buttonText and onButtonClick are provided
  const showButton = buttonText && onButtonClick;

  return (
    <AppBoxComponent
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: { xs: "300px", sm: "400px", md: "500px" },
        px: { xs: theme.spacing(3), sm: theme.spacing(4) },
        py: { xs: theme.spacing(4), sm: theme.spacing(6) },
        textAlign: "center",
        ...containerSx,
      }}
    >
      {/* Icon Container
       */}
      {/* <img
        src={Nodisplay}
        alt="no-data"
        style={{
          width: "auto",
        
        }}
        // 👇 use AppBoxComponent sx if you want theme-based spacing
      /> */}
      <AppBoxComponent
        component="img"
        src={Nodisplay}
        alt="no-data"
        sx={{
          height: imageHeight || { xs: '120px', sm: '160px', md: '180px' },
          mb: { xs: 1, sm: 1, md: 2 }, // 👈 adds margin between image and title
        }}
      />

      {/* Title */}
      {title && (
        <AppTypographyComponent
          sx={{
            color:
              theme.palette.custom?.text?.heading || theme.palette.common.white,
            fontSize: {
              xs: typography.fontSize.subheading1,
              sm: typography.fontSize.h3,
              md: typography.fontSize.h3,
            },
            fontWeight: typography.fontWeight.semibold,
            fontFamily: typography.fontFamily,
            mb: { xs: theme.spacing(1), sm: theme.spacing(1) },
            lineHeight: 1.3,
            ...titleSx,
          }}
        >
          {title}
        </AppTypographyComponent>
      )}
      {/* Subtitle */}
      {subtitle && (
        <AppTypographyComponent
          sx={{
            color: theme.palette.custom?.text?.paragraph || "#B0B6C3",
            fontSize: {
              xs: typography.fontSize.label,
              sm: typography.fontSize.paragraph,
              md: typography.fontSize.paragraph,
            },
            fontWeight: typography.fontWeight.regular,
            fontFamily: typography.fontFamily,
            maxWidth: { xs: "280px", sm: "400px", md: "500px" },
            mb: showButton ? { xs: theme.spacing(3), sm: theme.spacing(4) } : 0,
            lineHeight: 1.6,
            ...subtitleSx,
          }}
        >
          {subtitle}
        </AppTypographyComponent>
      )}

      {/* Button - Only show if buttonText and onButtonClick are provided */}
      {showButton && (
        <AppButtonComponent
          variant="contained"
          onClick={onButtonClick}
          sx={{
            mt: { xs: theme.spacing(2), sm: theme.spacing(3) },
            backgroundColor: theme.palette.primary.main || "#1455FF",
            color: theme.palette.common.white,
            fontWeight: typography.fontWeight.semibold,
            textTransform: "none",
            borderRadius: theme.spacing(1),
            fontSize: {
              xs: typography.fontSize.label,
              sm: typography.fontSize.paragraph,
            },
            padding: {
              xs: `${theme.spacing(1.25)} ${theme.spacing(2.5)}`,
              sm: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
            },
            minWidth: { xs: theme.spacing(20), sm: theme.spacing(25) },
            "&:hover": {
              backgroundColor: theme.palette.primary.dark || "#0F3FD6",
            },
          }}
        >
          {buttonText}
        </AppButtonComponent>
      )}
    </AppBoxComponent>
  );
};

NodisplayComponent.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  onButtonClick: PropTypes.func,
  containerSx: PropTypes.object,
  titleSx: PropTypes.object,
  subtitleSx: PropTypes.object,
  imageHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

NodisplayComponent.defaultProps = {
  buttonText: undefined,
  onButtonClick: undefined,
  containerSx: {},
  titleSx: {},
  subtitleSx: {},
  imageHeight: null,
};

export default NodisplayComponent;
